import { useSelector } from 'react-redux';

import {
  AuthRolesAndExperimentalFeatures,
  EnterpriseRoleEnum,
  FeatureEnum,
  PermissionEnum,
} from '@/graphql/generated';
import {
  getActiveEnterprise,
  isActiveEnterpriseLoading,
} from '@/slices/enterprises/enterprise.selector';

import { useCurrentUser } from '@/providers/CurrentUserProvider';

const permissionHierarchy: Record<PermissionEnum, number> = {
  [PermissionEnum.None]: 0,
  [PermissionEnum.Read]: 1,
  [PermissionEnum.Write]: 2,
};

export function usePermissionChecker() {
  const { currentUser, permissions } = useCurrentUser();
  const enterprise = useSelector(getActiveEnterprise);
  const isLoadingEnterprise = useSelector(isActiveEnterpriseLoading);

  const isGranted = (
    feature: FeatureEnum,
    requiredPermission: PermissionEnum = PermissionEnum.Read,
    strictComparison = false,
    enterpriseId?: string,
  ): boolean => {
    if (!enterpriseId && !enterprise) {
      return false;
    }

    const id = enterpriseId ?? enterprise?.id;

    if (enterprise && id === enterprise.id && enterprise.isTemplate) {
      return (
        (hasRole(AuthRolesAndExperimentalFeatures.Administrator) ||
          hasRole(AuthRolesAndExperimentalFeatures.Coach)) &&
        enterprise.ownedBy === currentUser?.company?.id
      );
    }

    const enterprisePermissions = permissions.find(
      (permission) => permission.enterpriseId === id,
    );

    if (!enterprisePermissions) {
      return false;
    }

    const userPermission = enterprisePermissions.permissions[feature];

    if (!userPermission) {
      return false;
    }

    if (strictComparison) {
      return userPermission === requiredPermission;
    }

    return (
      permissionHierarchy[userPermission] >=
      permissionHierarchy[requiredPermission]
    );
  };

  const hasRole = (role: AuthRolesAndExperimentalFeatures) => {
    if (!currentUser?.auth) {
      return false;
    }

    return (
      currentUser.auth.roles.includes(
        AuthRolesAndExperimentalFeatures.Superadmin,
      ) || currentUser.auth.roles.includes(role)
    );
  };

  const hasEnterpriseRole = (role: EnterpriseRoleEnum) => {
    return (
      (enterprise &&
        currentUser?.userAccesses?.some(
          (userAccess) =>
            userAccess.enterprise?.id === enterprise?.id &&
            userAccess.role.role === role,
        )) ||
      false
    );
  };

  return {
    isGranted,
    hasRole,
    loading: !currentUser || isLoadingEnterprise,
    hasEnterpriseRole,
  };
}
